import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useEnvContext } from "context/EnvContext";
import { APP_CAREPROVIDER, TRACK_EVENTS } from "core/consts";
import { getRequestOrigin } from "core/model/requests";
import { getKey } from "core/model/utils/strings";
import { AuctionRequest, Event } from "core/types";
import { PrintButtonConfirmedSchema } from "core/validationSchemas/tracking";
import { format, fromUnixTime } from "date-fns";
import { CheckboxInputFieldPresenter } from "ds_legacy/components/CheckboxInputField";
import RSButton from "ds_legacy/components/RSButton";
import { ICON_DARK } from "ds_legacy/materials/colors";
import { Divider, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, sizing } from "ds_legacy/materials/metrics";
import { Body, FONT_SIZE_20, Title } from "ds_legacy/materials/typography";
import { EventSpecMessage } from "dsl/atoms/EventSpecs";
import { useGetEventsSpecs } from "dsl/atoms/EventSpecs/utils";
import { usePrintWindow } from "dsl/atoms/PrintButton";
import { useModal, useQueryParam } from "dsl/hooks";
import EventCardCareprovider from "dsl/molecules/Messenger/EventCard/EventCardCareprovider";
import EventCardCareseeker from "dsl/molecules/Messenger/EventCard/EventCardCareseeker";
import { MessengerSpecMessage } from "dsl/molecules/Messenger/EventCard/EventMessagingBubble";
import { PrinterIcon } from "lucide-react";
import { Dispatch, Fragment, SetStateAction, useCallback } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Translations from "translations/types";

export type PrintParamState = {
  [index in (typeof PRINT_PARAM_KEYS)[number]]?: boolean;
};

export type PrintParams = [
  PrintParamState,
  Dispatch<SetStateAction<PrintParamState>>,
];

export const PRINT_PARAM_PATIENT_INFO = "patientInfo" as const;
export const PRINT_PARAM_PROVIDER_INFO = "providerInfo" as const;
export const PRINT_PARAM_CHAT_HISTORY = "chatHistory" as const;
export const PRINT_PARAM_REQUEST_NOTES = "requestNotes" as const;

export const PRINT_PARAM_KEYS = [
  PRINT_PARAM_PATIENT_INFO,
  PRINT_PARAM_PROVIDER_INFO,
  PRINT_PARAM_CHAT_HISTORY,
  PRINT_PARAM_REQUEST_NOTES,
] as const;

export const usePrintParams = () => {
  const { isReceiverApp } = useEnvContext();
  const defaultState: PrintParamState = {
    [PRINT_PARAM_CHAT_HISTORY]: false,
  };
  if (isReceiverApp) {
    defaultState[PRINT_PARAM_PATIENT_INFO] = true;
    defaultState[PRINT_PARAM_REQUEST_NOTES] = false;
  } else {
    defaultState[PRINT_PARAM_PROVIDER_INFO] = true;
  }
  return {
    printParams: useQueryParam<PrintParamState>(defaultState),
  };
};

const PrintParamCheckBox = ({
  label,
  name,
  printParamState,
  setPrintParamState,
}: {
  label: string;
  name: (typeof PRINT_PARAM_KEYS)[number];
  printParamState: PrintParamState;
  setPrintParamState: Dispatch<SetStateAction<PrintParamState>>;
}) => {
  return (
    <CheckboxInputFieldPresenter
      label={label}
      value={!!printParamState?.[name]}
      onChange={(value: boolean) =>
        setPrintParamState((currentState) => ({
          ...currentState,
          [name]: value,
        }))
      }
    />
  );
};

export const PrintMessengerFab = ({
  auctionRequest,
  printParams,
}: {
  auctionRequest: AuctionRequest;
  printParams: PrintParams;
}) => {
  const [open, setOpen] = useModal();
  const { app } = useEnvContext();
  const isCareproviderApp = app === APP_CAREPROVIDER;
  const [printParamState, setPrintParamState] = printParams;
  const { trackEvent } = useTracking<PrintButtonConfirmedSchema>();
  const { isAdmissionsRequest } = getRequestOrigin(auctionRequest);

  const translations = useTranslations();

  const closeForm = () => setOpen(false);

  const openPrintPreview = useCallback(() => {
    const uri = window.location.search;
    const prefix = uri.includes("?") ? "&" : "?";

    window.open(`${window.location}${prefix}print`, "_blank");
    setOpen(false);
  }, []);

  return (
    <>
      <IconButton
        size="large"
        onClick={() => {
          trackEvent({ name: TRACK_EVENTS.PRINT_BUTTON });
          setOpen(true);
        }}
      >
        <PrinterIcon
          size={FONT_SIZE_20}
          style={{ color: ICON_DARK, height: sizing(2.5), width: sizing(2.5) }}
        />
      </IconButton>
      <Dialog fullWidth open={open}>
        <DialogTitle>{translations.general.printModal.title}</DialogTitle>
        <DialogContent>
          <VerticalLayout>
            {isCareproviderApp ? (
              <PrintParamCheckBox
                label={translations.general.printModal.receiver.patientProfile}
                name={PRINT_PARAM_PATIENT_INFO}
                printParamState={printParamState}
                setPrintParamState={setPrintParamState}
              />
            ) : (
              <PrintParamCheckBox
                label={translations.general.printModal.sender.informationAboutProvider(
                  { providerName: auctionRequest.careprovider?.name || "" },
                )}
                name={PRINT_PARAM_PROVIDER_INFO}
                printParamState={printParamState}
                setPrintParamState={setPrintParamState}
              />
            )}
            <PrintParamCheckBox
              label={translations.general.printModal.sender.chatHistory}
              name={PRINT_PARAM_CHAT_HISTORY}
              printParamState={printParamState}
              setPrintParamState={setPrintParamState}
            />
            {isCareproviderApp && isAdmissionsRequest && (
              <PrintParamCheckBox
                label={
                  translations.careproviderApp.recareAdmissionsProfile
                    .notesSection.title
                }
                name={PRINT_PARAM_REQUEST_NOTES}
                printParamState={printParamState}
                setPrintParamState={setPrintParamState}
              />
            )}
          </VerticalLayout>
        </DialogContent>
        <DialogActions>
          <RSButton
            className="cancel"
            color="primary"
            id="cancel"
            loading="na"
            variant="text"
            onClick={closeForm}
          >
            {translations.actions.cancel}
          </RSButton>
          <RSButton
            color="primary"
            disabled={!Object.values(printParamState || {}).some(Boolean)}
            id="confirm"
            loading="na"
            variant="text"
            onClick={() => {
              trackEvent({
                name: TRACK_EVENTS.PRINT_BUTTON_CONFIRMED,
                print_chat_history: !!printParamState.chatHistory,
                print_profile_information: !!(isCareproviderApp
                  ? printParamState.patientInfo
                  : printParamState.providerInfo),
              });
              openPrintPreview();
            }}
          >
            {translations.actions.confirm}
          </RSButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PrintMessageBubble = ({
  eventMessages,
  messagesToDisplay,
}: {
  eventMessages?: EventSpecMessage[];
  messagesToDisplay: string[] | null;
}) => {
  return (
    <>
      {eventMessages?.map((msg, idx) => {
        return (
          <VerticalLayout key={idx?.toString()} margin={margin(1, 0, 0, 0)}>
            <MessengerSpecMessage eventMessage={msg} />
          </VerticalLayout>
        );
      })}
      {messagesToDisplay?.map((msg, i) => <Body key={getKey(i)}>{msg}</Body>)}
    </>
  );
};

export const PrintableEventCard = ({
  eventMessages,
  messagesToDisplay,
  statusHeader,
  timestamp,
}: {
  eventMessages?: EventSpecMessage[];
  messagesToDisplay: string[] | null;
  statusHeader: string | undefined;
  timestamp: number | null | undefined;
}) => {
  return (
    <VerticalLayout
      style={{
        pageBreakInside: "avoid",
      }}
    >
      <VerticalLayout>
        <Body margin={margin(0)}>
          {timestamp && format(fromUnixTime(timestamp), "d LLLL y hh:mm")}
        </Body>
        <Body margin={margin(0)} whiteSpace="normal">
          {statusHeader}
        </Body>
      </VerticalLayout>
      <PrintMessageBubble
        eventMessages={eventMessages}
        messagesToDisplay={messagesToDisplay}
      />
      <Divider margin={margin(2, 0, 0, 0)} />
    </VerticalLayout>
  );
};

const PrintMessages = ({
  auctionRequest,
  events,
  forCareprovider,
}: {
  auctionRequest: AuctionRequest | undefined;
  events: Event[] | undefined;
  forCareprovider: boolean;
}) => {
  const enhancedEvents = useGetEventsSpecs({
    auction: auctionRequest?.auction,
    auctionRequest,
    eventLocation: "messenger",
    events,
  });

  if (!auctionRequest || !enhancedEvents) return null;

  return (
    <div className="ml-4">
      {enhancedEvents.map((event, index) => {
        return (
          <Fragment key={getKey(event.id, index)}>
            {forCareprovider ? (
              <EventCardCareprovider
                auctionRequest={auctionRequest}
                event={event}
                isDividerVisible={false}
                print
                shouldShowBefore={false}
              />
            ) : (
              <EventCardCareseeker
                auctionRequest={auctionRequest}
                event={event}
                isDividerVisible={false}
                print
                shouldShowBefore={false}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

const PrintMessenger = ({
  auctionRequest,
  didPrintInfo,
  encryptionError,
  events,
  forCareprovider,
  hasEncryption,
  translations,
  userSupportsEncryption,
}: {
  auctionRequest: AuctionRequest;
  didPrintInfo: boolean;
  encryptionError: string | undefined;
  events: Event[];
  forCareprovider: boolean;
  hasEncryption: boolean | undefined;
  translations: Translations;
  userSupportsEncryption: boolean | undefined;
}) => {
  usePrintWindow(didPrintInfo);
  if (!hasEncryption || !userSupportsEncryption || encryptionError) {
    console.error("unable to print chat history\n", {
      encryptionError,
      hasEncryption,
      userSupportsEncryption,
    });
    return null;
  }

  return (
    <VerticalLayout
      style={{
        pageBreakInside: "avoid",
      }}
    >
      <Title>{translations.auctionRequest.messengerTab}</Title>
      <PrintMessages
        auctionRequest={auctionRequest}
        events={events}
        forCareprovider={forCareprovider}
      />
    </VerticalLayout>
  );
};

export default PrintMessenger;
